// © 2015-2021 WDNA - All Rights Reserved

import _ from 'lodash';
import * as React from 'react';
import * as L from 'leaflet';
import G from 'google-map-react';

import { EventSelect2 } from 'wdna-select2';
import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import MapModal from 'wdna-components/lib/modals/generics/MapModal';
import { messageError, messageWarning, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';
import { hasLevel } from 'wdna-components/lib/helpers/permission-new';

import { company, location, CompanyDO, LocationDO } from '../../../management/requests';
import { typeSensor, TypeSensorDO, lov, LovDO } from '../../../configuration/requests';
import { sensor, waste, SensorDO, WasteContainerDO } from '../../requests';

import ButtonsForm from '../../../../components/ButtonsForm';
import WasteForm from '../waste/Form';
import WasteEdit from '../waste/Edit';
import ChripStack from './ChirpStack';
import Form from './Form';

const GROUP_TYPES: Record<string, number[]> = {
  WST: [], // Waste types
  WHR: [], // Weather types
};
const MAX_VAL: number = 3600 * 24;
const TYPE_TAB_ID: string = 'WE';

const MIN_UNLOADS: Map<string, number> = new Map<string, number>([
  ['FOR', 10],
  ['GLA', 10],
  ['PAP', 7],
  ['PET', 7],
  ['PNG', 10],
  ['CRK', 10],
  ['SUW', 10],
]);

const MAX_WEIGHT: Map<string, number> = new Map<string, number>([
  ['120', 60],
  ['240', 100],
  ['800', 320],
  ['1000', 400],
]);

interface Props {
  rowId?: number;
  notify: () => void;
  duplicate: boolean;
  sensorList: SensorDO[];
}

interface State {
  loading: boolean;
  isNew: boolean;
  isChirpStack: boolean;
  showMap: boolean;
  showChirpStack: boolean;

  typeSensorArray: TypeSensorDO[];
  companiesArray: CompanyDO[];
  locationArray: LocationDO[];
  statusArray: LovDO[];
  groupsArray: LovDO[];

  modelWasteArray?: LovDO[];
  typeWasteArray?: LovDO[];

  timetable: {
    min: number;
    max: number;
  };

  data: Partial<SensorDO>;
  dtype: Partial<WasteContainerDO>;

  dinfo: {
    containerId?: number;
    date: Date;
    ratio1: number;
    ratio2: number;
    ratio3: number;
    ratio4: number;
  };
}

/**
 * Function to check if a sensor is of gropu type indicated
 *
 * @param type string code defining the group type
 * @param data information of the sensor to check
 * @returns boolean with true if is of the indicated type false otherwise
 */
function isGroupType(type: string, data: Partial<SensorDO> | undefined): boolean {
  return (data?.typeSensorId && (GROUP_TYPES[type]?.includes(_.toNumber(data.typeSensorId)))) || false;
}

export default class Edit extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;
  private defaultValues: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {};

    this.state = {
      loading: false,
      isNew: true,
      isChirpStack: true,
      showMap: false,
      showChirpStack: false,

      typeSensorArray: [],
      companiesArray: [],
      locationArray: [],
      statusArray: [],
      groupsArray: [],

      modelWasteArray: [],
      typeWasteArray: [],

      timetable: { min: 0, max: 0 },

      data: {
        persistent1: false,
        persistent2: false,
        update: false,
        lastDate: new Date(),
        command1: '',
        command2: '',
        nocxTime: 60,
        nocxTimetableList: [
          [0, MAX_VAL],
        ],
      },

      dtype: {
        date: new Date(),
        minUnload: 0,
        maxWeight: 0,
        minTemp: 1,
        maxTemp: 50,
        active: true
      },

      dinfo: {
        date: new Date(),
        ratio1: 0,
        ratio2: 0,
        ratio3: 0,
        ratio4: 0,
      },
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  }

  public componentWillUnmount(): void {
    window.store.dispatch(removeTab(TYPE_TAB_ID));
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public load = async (): Promise<void> => {
    const { rowId, duplicate } = this.props;
    const isNew: boolean = (!_.isFinite(rowId) || duplicate);

    this.loadingStart();
    try {
      const doWaste: Partial<WasteContainerDO> = this.state.dtype;
      const [
        typeSensorArray,
        companiesArray,
        locationArray,
        statusArray,
        groupsArray,
        modelWasteArray,
        typeWasteArray,
      ] = await Promise.all([
        typeSensor<TypeSensorDO[]>('list', undefined, { active: true }, { params: { order: 'typeModule' } }) || [],
        company<CompanyDO[]>('list') || [],
        location<LocationDO[]>('list') || [],
        lov<LovDO[]>('statics', 'sensor_status') || [],
        lov<LovDO[]>('statics', 'sensor_group') || [],
        lov<LovDO[]>('statics', 'model_container') || [],
        lov<LovDO[]>('statics', 'type_waste') || [],
      ]);
      let doSensor: Partial<SensorDO> | undefined = this.state.data;
      if (_.isFinite(rowId)) {
        doSensor = await sensor<SensorDO>('get', String(rowId));
      }

      // Update special TYPES
      typeSensorArray.forEach((item: TypeSensorDO) => {
        if (item.type === 'WST') GROUP_TYPES.WST.push(item.id);
        if (item.type === 'WHR') GROUP_TYPES.WHR.push(item.id);
        if (item.type === 'SLT') GROUP_TYPES.WHR.push(item.id);
      });

      if (!_.isUndefined(doSensor)) {
        if (duplicate) {
          _.unset(doSensor, 'code');
          _.unset(doSensor, 'name');
          _.unset(doSensor, 'chirpstack');
        }
        const { timetable } = this.state;
        if (doSensor.nocxTimetableList) {
          timetable.min = doSensor.nocxTimetableList[0][0] / 3600;
          timetable.max = doSensor.nocxTimetableList[0][1] / 3600;
        }

        const isChirpStack: boolean = isNew || !_.isEmpty(doSensor.chirpstack?.eui);

        this.setLocalState(
          {
            data: { ...doSensor, },
            dtype: { ...doWaste, },
            typeSensorArray,
            companiesArray,
            locationArray,
            statusArray,
            groupsArray,
            modelWasteArray,
            typeWasteArray,
            timetable,
            isNew,
            isChirpStack,
          },
          () => {
            this.defaultValues = _.cloneDeep(doSensor);
          }
        );
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadType = (): void => {
    const { rowId } = this.props;
    const { data } = this.state;

    if (isGroupType('WST', data)) {
      const title: string = rowId ? `Waste-Container Edit: ${rowId}` : 'Waste-Container New';

      window.store.dispatch(removeTab(TYPE_TAB_ID));
      const tabData: TabConfig = {
        id: TYPE_TAB_ID,
        active: true,
        title,
        component: WasteEdit,
        props: {
          rowId,
          tabId: this.pid,
          report: 'WASTE CONTAINERS',
          title: 'WasteContainers',
        },
      };
      window.store.dispatch(addTab(tabData));
    } else {
      messageWarning('Warning', 'There are no additional edition for this sensor type');
    }
  };

  public loadingStart = (): void => {
    this.setLocalState({ loading: true });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setLocalState({ loading: false });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setLocalState({ data: { ...this.defaultValues } });
  };

  public submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const { duplicate } = this.props;
    const { isNew, data, dtype } = this.state;
    if (duplicate) data.id = undefined;
    const isWaste: boolean = isGroupType('WST', data);

    data.lastDate = new Date();
    data.nocxTimetableList = this.formatTimetable();
    if (!this.isValidForm1(data)) return;
    if (isNew && isWaste && !this.isValidForm2(dtype)) return;

    this.loadingStart();
    try {
      const saved: SensorDO | undefined = await sensor<SensorDO>('set', undefined, data);

      // Sensor type save data
      if (saved?.id && isNew) {
        // Waste Type
        if (isGroupType('WST', saved)) {
          const { dinfo } = this.state;
          dtype.id = saved.id;
          dinfo.containerId = saved.id;
          dinfo.date = dtype.date || new Date();
          await waste('set', undefined, dtype);
          await waste('info_set', undefined, dinfo);
        }
      }
      messageInfo('Success', `Sensor successfully ${data.id ? 'edited' : 'created'} ${saved?.name}`, 5000);
      const { notify } = this.props;
      if (typeof notify === 'function') {
        notify();
      }
      window.store.dispatch(removeTab('SE'));
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public isValidForm1 = (data: Partial<SensorDO>): boolean => {
    // eslint-disable-next-line no-shadow
    const { status, typeSensorId, companyId, locationId, code } = data;
    const { rowId, sensorList = [] } = this.props;
    if (code?.length !== 7) {
      messageError('Error', 'Incorrect sensor code, must have 7 digits');
      return false;
    }
    if (!status) {
      messageError('Error', 'Please select the status of the sensor');
      return false;
    }
    if (!typeSensorId) {
      messageError('Error', 'Please select a type of sensor');
      return false;
    }
    if (!companyId) {
      messageError('Error', 'Please select a company of sensor');
      return false;
    }
    if (!locationId) {
      messageError('Error', 'Please select a location of sensor');
      return false;
    }
    // Check only for new entries
    if (rowId === undefined || isNaN(rowId)) {
      // Non repeated codes
      const usedCodes: Set<string> = sensorList.reduce((result: Set<string>, item: SensorDO) => {
        if (item.typeSensorId === +typeSensorId) result.add(item.code);
        return result;
      }, new Set([]));
      const isDuplicateCode: boolean = usedCodes.has(code);
      if (isDuplicateCode) {
        messageWarning('Warning', 'The device code you entered is already in use');
        return false;
      }
    }
    // ChripStark info
    if (!_.isEmpty(data?.chirpstack?.eui) && _.isEmpty(data?.chirpstack?.key)) {
      messageWarning('Warning', 'You must insert a key for chirpstack activation');
      return false;
    }

    return true;
  };

  public isValidForm2 = (dtype: Partial<WasteContainerDO>): boolean => {
    const { type, model } = dtype;

    if (!type) {
      messageError('Error', 'Please select the type of the container');
      return false;
    }
    if (!model) {
      messageError('Error', 'Please select the model of the container');
      return false;
    }

    return true;
  };

  public formatTimetable = (): [number, number][] => {
    const { timetable } = this.state;
    const timetableList: [number, number][] = [
      [(timetable.min * 3600), (timetable.max * 3600)],
    ];
    return timetableList;
  };

  public handleChangeRange = (name: string, value: any): void => {
    this.setLocalState({ [name]: value });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = name.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public handleChangeWaste = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | Date | null): void => {
    if (e instanceof Date) {
      this.setState((prevState: State) => ({
        dtype: {
          ...prevState.dtype,
          date: e,
        },
      }));
    } else {
      const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
      const { ctype } = dataset || {};
      // New data path
      const path: string = name.replace('data', 'dtype');
      // Process new value from ctype
      let newValue: any = (type === 'checkbox') ? !!checked : value;
      if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
      // Create new data state structure
      const levels: string[] = path.split('.');
      const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
      _.set(nextState, levels, newValue);

      // Calculated values from other params
      if (path === 'dtype.type') {
        let min: number = 0;
        if (_.includes(['SUW', 'FOR', 'GLA', 'PNG', 'CRK'], value)) min = 10;
        if (_.includes(['PAP', 'PET'], value)) min = 7;
        _.set(nextState, ['dtype', 'minUnload'], min);
      }
      if (path === 'dtype.size') {
        let max: number = 0;
        if (value === '120') max = 60;
        if (value === '240') max = 100;
        if (value === '800') max = 320;
        if (value === '1000') max = 400;
        _.set(nextState, ['dtype', 'maxWeight'], max);
      }

      this.setLocalState(nextState);
    }
  };

  public handleChangeInfo = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { name, value, checked, type, dataset } = (e as React.ChangeEvent<HTMLInputElement>).target;
    const { ctype } = dataset || {};
    // New data path
    const path: string = name.replace('info', 'dinfo');
    // Process new value from ctype
    let newValue: any = (type === 'checkbox') ? !!checked : value;
    if (ctype === 'number') newValue = (/^\d+(\.\d+){0,1}$/.test(value)) ? _.toNumber(value) : null;
    // Create new data state structure
    const levels: string[] = path.split('.');
    const nextState: Record<string, any> = { [levels[0]]: { ...this.state[levels[0]] } };
    _.set(nextState, levels, newValue);
    this.setLocalState(nextState);
  };

  public handleChangeUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.handleChange(e);
    const { checked } = e.target;
    if (!checked) {
      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          firmwareId: null,
        },
      }));
    }
  };

  public handleChangeLocation = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const { value } = e.target;
    const { data, locationArray } = this.state;

    this.handleChange(e);

    let { latitude, longitude } = data;
    if (!_.isEmpty(value) && (!_.isNumber(latitude) || !_.isNumber(longitude))) {
      const loc: LocationDO | undefined = locationArray?.find((item: LocationDO) => item.id === Number(value));
      latitude = loc?.latitude;
      longitude = loc?.longitude;

      this.setState((prevState: State) => ({
        data: {
          ...prevState.data,
          latitude,
          longitude
        },
      }));
    }
  };

  public handleChangeMultiple = (e: EventSelect2): void => {
    const { id, selected } = e.params.data;
    if (selected) {
      this.addValue(id);
    } else {
      this.remValue(id);
    }
  };

  public addValue = (id: string): any => {
    this.setState((prevState: State) => {
      const { data } = prevState;
      const newList: string[] = _.cloneDeep(data?.groupsList) || [];
      if (newList.includes(id)) return prevState;
      return { data: { ...data, groupsList: [...newList, id] } };
    });
  };

  public remValue = (id: string): any => {
    this.setState((prevState: State) => {
      const { data } = prevState;
      const newList: string[] = _.cloneDeep(data?.groupsList) || [];
      return { data: { ...data, groupsList: newList.filter((item: any) => item !== id) } };
    });
  };

  public handleMapClick = (e: L.LeafletMouseEvent | G.ClickEventValue): void => {
    const lat: number = (e as G.ClickEventValue).lat || (e as L.LeafletMouseEvent).latlng.lat;
    const lng: number = (e as G.ClickEventValue).lng || (e as L.LeafletMouseEvent).latlng.lng;
    this.setState((prevState: State) => ({
      data: {
        ...prevState.data,
        latitude: lat,
        longitude: lng,
      },
    }));
  };

  public openMap = (): void => {
    this.setLocalState({ showMap: true });
  };

  public closeMap = (): void => {
    this.setLocalState({ showMap: false });
  };

  // TODO: duplicated code ??
  public autoFormMin = (data: WasteContainerDO): number => {
    const { type } = data;
    return MIN_UNLOADS.get(type) || 0;
  };

  public autoFormMax = (data: WasteContainerDO): number => {
    const { size } = data;
    return MAX_WEIGHT.get(String(size)) || 0;
  };

  public genEUI = (): void => {
    const { data } = this.state;
    if (data.code != null) {
      _.set(data, ['chirpstack', 'eui'], _.padStart(_.padStart(parseInt(data.code, 10).toString(16), 6, '0'), 16, 'f'));
      this.setLocalState({ data });
    }
  };

  public openChirpStack = (): void => {
    this.setLocalState({ showChirpStack: true });
  };

  public closeChirpStack = (): void => {
    this.setLocalState({ showChirpStack: false });
  };

  public render(): JSX.Element {
    const { data, loading, showMap, showChirpStack } = this.state;
    const { duplicate } = this.props;

    const isNew: boolean = (data.id === undefined || !!isNaN(data.id));
    const isWaste: boolean = isGroupType('WST', data);
    const isWeatherLink: boolean = isGroupType('WHR', data);

    let mapZoom: number = 6;
    let mapInitialMapCenter: [number, number] = [40.6, 1.13];
    if (_.isNumber(data.latitude) && _.isNumber(data.longitude)) {
      mapZoom = 10;
      mapInitialMapCenter = [data.latitude, data.longitude];
    }

    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div className="widget-body">
                  <Loader show={loading} overlay={false} />
                  <UiValidate>
                    <form
                      className="smart-form"
                      onSubmit={(e: React.FormEvent<HTMLFormElement>): void => { this.submit(e).catch(() => { /* pass */ }); }}
                      method="post"
                      encType="multipart/form-data"
                    >
                      <header>
                        <h2>
                          <span className="widget-icon" style={{ padding: '8px' }}>
                            <i className="fa fa-edit" />
                          </span>
                          Sensor {(data.id && !this.props.duplicate) ? `Edit: ${data.id}` : 'New'}
                        </h2>
                      </header>
                      <Form
                        formValues={{ ...this.state, isWeatherLink }}
                        onChange={this.handleChange}
                        onChangeRange={this.handleChangeRange}
                        onChangeUpdate={this.handleChangeUpdate}
                        onChangeLocation={this.handleChangeLocation}
                        onChangeMultiple={this.handleChangeMultiple}
                        openMap={this.openMap}
                        loadType={this.loadType}
                        genEUI={this.genEUI}
                        openChirpStack={this.openChirpStack}
                        duplicate={duplicate}
                      />
                      {((isNew && isWaste) || (duplicate && isWaste)) && (
                        <div>
                          <header>
                            <h2>
                              <span className="widget-icon" style={{ padding: '8px' }}>
                                <i className="fa fa-edit" />
                              </span>
                              Waste Container Info
                            </h2>
                          </header>
                          <WasteForm
                            onChange={this.handleChangeWaste}
                            onChangeInfo={this.handleChangeInfo}
                            formValues={this.state as any}
                            duplicate={duplicate}
                            onAutoFormMin={this.autoFormMin}
                            onAutoFormMax={this.autoFormMax}
                          />
                        </div>
                      )}
                      <MapModal
                        show={showMap}
                        zoom={mapZoom}
                        onCancel={this.closeMap}
                        onMapClick={this.handleMapClick}
                        title="Select coordinates of the sensor"
                        initialMapCenter={mapInitialMapCenter}
                        latitude={data.latitude || 0}
                        longitude={data.longitude || 0}
                        useGoogleMaps={false}
                      />
                      {data.id && !_.isEmpty(data.chirpstack?.eui) && (
                        <ChripStack
                          show={showChirpStack}
                          sensorId={data.id}
                          onCancel={this.closeChirpStack}
                          title="Manage chirpstack device queue"
                        />
                      )}
                      <ButtonsForm loading={loading} disabled={!hasLevel('role', ['ADM', 'URW'])} onReset={this.onReset} />
                    </form>
                  </UiValidate>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
